import { ensureCustomFilterStyle } from './filterStyles';
import { parseYearFromUseApproveDate } from './filterUtils';

/**
 * 연차 필터 (사용승인일 기준)
 * 사용 예: { filter: BuiltYearFilter }
 */
class BuiltYearFilter {
  init(params) {
    ensureCustomFilterStyle();
    this.params = params;
    this.model = null;
    this.onClose = params.onClose || null;
    this.min = 1960;
    this.max = new Date().getFullYear();
    this.from = this.min;
    this.to = this.max;
    this.gui = document.createElement('div');
    this.gui.className = 'aptrank-custom-filter';
    this.render();
  }

  render() {
    const title = this.model ? `${this.from} ~ ${this.to}` : '전체';
    this.gui.innerHTML = `
      <button type="button" class="complex-top-filter-panel-close" style="position: absolute; top: 8px; right: 8px; border: none; background: transparent; color: #64748b; width: 28px; height: 28px; border-radius: 8px; font-size: 24px; line-height: 1; cursor: pointer; z-index: 10;">×</button>
      <div class="aptrank-custom-filter-title">${title}</div>
      <div class="aptrank-custom-filter-slider-wrap">
        <div class="aptrank-custom-filter-slider-track"></div>
        <input class="aptrank-custom-filter-slider from" type="range" min="${this.min}" max="${this.max}" value="${this.from}" />
        <input class="aptrank-custom-filter-slider to" type="range" min="${this.min}" max="${this.max}" value="${this.to}" />
      </div>
      <div class="aptrank-custom-filter-grid">
        <button class="aptrank-custom-filter-chip" data-age="0">입주예정</button>
        <button class="aptrank-custom-filter-chip" data-age="2">2년</button>
        <button class="aptrank-custom-filter-chip" data-age="4">4년</button>
        <button class="aptrank-custom-filter-chip" data-age="10">10년</button>
        <button class="aptrank-custom-filter-chip" data-age="15">15년</button>
        <button class="aptrank-custom-filter-chip" data-age="20">20년</button>
        <button class="aptrank-custom-filter-chip" data-age="25">25년</button>
        <button class="aptrank-custom-filter-chip" data-age="30">30년</button>
      </div>
      <div class="aptrank-custom-filter-actions">
        <button class="aptrank-custom-filter-reset" type="button">초기화</button>
        <button class="aptrank-custom-filter-apply" type="button">적용</button>
      </div>
    `;

    this.titleEl = this.gui.querySelector('.aptrank-custom-filter-title');
    const fromEl = this.gui.querySelector('input.from');
    const toEl = this.gui.querySelector('input.to');

    fromEl.addEventListener('input', (e) => {
      this.from = Math.min(Number(e.target.value), this.to);
      this.updateTitle();
      this.updateTrack();
    });

    toEl.addEventListener('input', (e) => {
      this.to = Math.max(Number(e.target.value), this.from);
      this.updateTitle();
      this.updateTrack();
    });

    this.gui.querySelectorAll('[data-age]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const age = Number(btn.getAttribute('data-age'));
        const year = new Date().getFullYear();
        if (age === 0) {
          // 입주예정: 현재 연도 이후
          this.from = Math.max(year, this.min);
          this.to = Math.min(year + 10, this.max);
        } else {
          this.from = Math.max(year - age, this.min);
          this.to = Math.min(year, this.max);
        }
        fromEl.value = this.from;
        toEl.value = this.to;
        this.updateTitle();
        this.updateTrack();
      });
    });

    this.gui.querySelector('.aptrank-custom-filter-apply').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.model = { from: this.from, to: this.to };
      this.params.filterChangedCallback();
    });

    this.gui.querySelector('.aptrank-custom-filter-reset').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.model = null;
      this.from = this.min;
      this.to = this.max;
      this.params.filterChangedCallback();
      this.render();
    });

    const closeBtn = this.gui.querySelector('.complex-top-filter-panel-close');
    if (closeBtn && this.onClose) {
      closeBtn.addEventListener('click', this.onClose);
    }

    this.updateTrack();
  }

  updateTitle() {
    if (!this.titleEl) return;
    if (this.from !== this.min || this.to !== this.max) {
      this.titleEl.textContent = `${this.from} ~ ${this.to}`;
    } else {
      this.titleEl.textContent = '전체';
    }
  }

  updateTrack() {
    const track = this.gui.querySelector('.aptrank-custom-filter-slider-track');
    if (!track) return;
    const range = this.max - this.min;
    const leftPercent = ((this.from - this.min) / range) * 100;
    const rightPercent = ((this.max - this.to) / range) * 100;
    track.style.left = `${leftPercent}%`;
    track.style.right = `${rightPercent}%`;
  }

  apply(shouldRerender = false) {
    this.model = { from: this.from, to: this.to };
    this.params.filterChangedCallback();
    if (shouldRerender) {
      this.render();
      return;
    }
    this.updateTitle();
  }

  getGui() {
    return this.gui;
  }

  isFilterActive() {
    return !!this.model;
  }

  doesFilterPass(p) {
    if (!this.model) return true;
    const year = parseYearFromUseApproveDate(p?.data?.useApproveDate);
    if (!Number.isFinite(year)) return false;
    return year >= this.model.from && year <= this.model.to;
  }

  getModel() {
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    if (model) {
      this.from = Number(model.from ?? this.min);
      this.to = Number(model.to ?? this.max);
    } else {
      this.from = this.min;
      this.to = this.max;
    }
    if (this.gui) this.render();
  }
}

export default BuiltYearFilter;
